import React from 'react';
import SecondaryButton from '../../button/SecondaryButton';
import './Pagination.css';

/**
 * Pagination Component
 * Standardized pagination controls
 * 
 * @param {number} currentPage - Current page number (1-based)
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Page change handler: (page: number) => void
 * @param {number} pageSize - Current page size
 * @param {Array} pageSizeOptions - Array of page size options (default: [5, 10, 25, 50, 100])
 * @param {function} onPageSizeChange - Page size change handler: (size: number) => void
 * @param {boolean} showPageSize - Show page size selector (default: true)
 * @param {string} className - Additional CSS classes
 */
const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  pageSize,
  pageSizeOptions = [5, 10, 25, 50, 100],
  onPageSizeChange,
  showPageSize = true,
  className = ''
}) => {
  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handlePageSizeChange = (e) => {
    if (onPageSizeChange) {
      onPageSizeChange(Number(e.target.value));
    }
  };

  return (
    <div className={`pagination-container ${className}`.trim()}>
      {showPageSize && pageSize && onPageSizeChange && (
        <div className="pagination-page-size">
          <label>Rows per page:</label>
          <select
            value={pageSize}
            onChange={handlePageSizeChange}
            className="pagination-page-size-select"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="pagination-controls">
        <SecondaryButton
          variant="outline"
          size="small"
          onClick={() => onPageChange && onPageChange(1)}
          disabled={currentPage === 1}
        >
          ««
        </SecondaryButton>
        <SecondaryButton
          variant="outline"
          size="small"
          onClick={() => onPageChange && onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ‹
        </SecondaryButton>
        {getVisiblePages().map((page, idx) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
                ...
              </span>
            );
          }
          return (
            <SecondaryButton
              key={page}
              variant={currentPage === page ? 'primary' : 'outline'}
              size="small"
              onClick={() => onPageChange && onPageChange(page)}
              className={currentPage === page ? 'active' : ''}
            >
              {page}
            </SecondaryButton>
          );
        })}
        <SecondaryButton
          variant="outline"
          size="small"
          onClick={() => onPageChange && onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          ›
        </SecondaryButton>
        <SecondaryButton
          variant="outline"
          size="small"
          onClick={() => onPageChange && onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          »»
        </SecondaryButton>
      </div>
    </div>
  );
};

export default Pagination;










